import styled from '@emotion/styled'
import gql from 'graphql-tag'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLazyQuery, useMutation, useQuery } from '../../../api/graphql'
import { Hugeicon } from '../../../icons/Hugeicon'

const SOCIAL_CAPTIONS_QUERY = gql`
  query GenerateSocialCaptions($eventId: ID!) {
    generateSocialCaptions(eventId: $eventId) {
      facebookCaption
      instagramCaption
      eventDescription
      eventLink
      isCustomerUrl
    }
  }
`

const REWRITE_CAPTION_QUERY = gql`
  query RewriteSocialCaption($eventId: ID!, $platform: String!) {
    rewriteSocialCaption(eventId: $eventId, platform: $platform)
  }
`

const SOCIAL_ACCOUNTS_QUERY = gql`
  query SocialAccountsForSharing {
    currentUser {
      id
      organizationMemberships {
        role
        organization {
          id
          socialAccounts {
            id
            platform
            platformAccountName
            platformAccountImageUrl
            status
          }
        }
      }
    }
  }
`

const CREATE_SOCIAL_POSTS_MUTATION = gql`
  mutation SocialPostCreate($posts: [SocialPostCreateInput!]!) {
    socialPostCreate(posts: $posts) {
      socialPosts {
        id
        status
        platform
      }
      errors {
        attribute
        message
      }
    }
  }
`

type SocialAccount = {
  id: string
  platform: string
  platformAccountName: string | null
  platformAccountImageUrl: string | null
  status: string
}

type PlatformConfig = {
  key: string
  label: string
  icon: React.ReactNode
  color: string
  maxChars: number
  hint: string
}

const PLATFORMS: PlatformConfig[] = [
  {
    key: 'facebook_page',
    label: 'Facebook',
    icon: <FacebookIcon />,
    color: '#1877f2',
    maxChars: 63_206,
    hint: 'Includes link to your event page',
  },
  {
    key: 'instagram_business',
    label: 'Instagram',
    icon: <InstagramIcon />,
    color: '#c026d3',
    maxChars: 2200,
    hint: 'Includes hashtags',
  },
]

const CLOUD_NAME = process.env.CLOUDINARY_CLOUD_NAME

function cloudinaryUrl(publicId: string, transform: string): string {
  return `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/${transform}/${publicId}`
}

// Full-size transforms for the actual post sent to Meta API
// Uses c_pad with border_gradient to avoid cropping — fills negative space with gradient from image edges
const PLATFORM_IMAGE_TRANSFORMS: Record<string, string> = {
  facebook_page: 'w_1200,h_630,c_pad,b_auto:border_gradient,q_auto,f_jpg',
  instagram_business: 'w_1080,h_1080,c_pad,b_auto:border_gradient,q_auto,f_jpg',
}

// Small transforms for the in-modal preview thumbnail
const PREVIEW_IMAGE_TRANSFORMS: Record<string, string> = {
  facebook_page: 'w_500,h_262,c_pad,b_auto:border_gradient,q_auto,f_jpg',
  instagram_business: 'w_300,h_300,c_pad,b_auto:border_gradient,q_auto,f_jpg',
}

type Step = 'select' | 'compose' | 'posted'

type Props = {
  eventId: string
  eventTitle: string
  eventImagePublicId?: string
  organizationId: string
  onClose: () => void
}

const SocialShareModal: React.FC<Props> = ({ eventId, eventTitle, eventImagePublicId, organizationId, onClose }) => { // eslint-disable-line max-len
  const { data: captionsData, loading: captionsLoading, error: captionsError } = useQuery(SOCIAL_CAPTIONS_QUERY, { variables: { eventId } }) // eslint-disable-line max-len
  const { data: accountsData, loading: accountsLoading, error: accountsError } = useQuery(SOCIAL_ACCOUNTS_QUERY, { variables: { organizationId } }) // eslint-disable-line max-len
  const [createPosts, { loading: posting }] = useMutation(CREATE_SOCIAL_POSTS_MUTATION)

  const [step, setStep] = useState<Step>('select')
  const [composeIndex, setComposeIndex] = useState(0)
  const [selectedAccounts, setSelectedAccounts] = useState<Record<string, boolean>>({})
  const [captions, setCaptions] = useState<Record<string, string>>({})
  const [scheduleMode, setScheduleMode] = useState(false)
  const [scheduledFor, setScheduledFor] = useState('')
  const [postError, setPostError] = useState<string | null>(null)
  const [rewriting, setRewriting] = useState(false)
  const [confirmingPost, setConfirmingPost] = useState(false)
  const rewriteAccountRef = React.useRef<string | null>(null)

  // Apollo v2: useLazyQuery execute doesn't return a Promise — use onCompleted/onError callbacks
  const [fetchAiCaption] = useLazyQuery(REWRITE_CAPTION_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (data: { rewriteSocialCaption?: string }) => {
      const accountId = rewriteAccountRef.current
      const caption = data?.rewriteSocialCaption
      if (caption && accountId) {
        setCaptions(prev => ({ ...prev, [accountId]: caption }))
      } else {
        setRewriteError('AI rewrite returned empty. You can edit the caption manually.')
      }
      setRewriting(false)
    },
    onError: (error: { message: string }) => {
      console.error('[SocialShare] AI rewrite failed:', error.message) // eslint-disable-line no-console
      setRewriteError('AI rewrite is not available right now. You can edit the caption manually.')
      setRewriting(false)
    },
  })

  const accounts: SocialAccount[] = useMemo(
    () =>
      accountsData?.currentUser?.organizationMemberships
        ?.find((m: { organization: { id: string } }) => m.organization.id === organizationId)
        ?.organization?.socialAccounts?.filter((a: SocialAccount) => a.status === 'active') || [],
    [accountsData, organizationId],
  )

  const accountsByPlatform = useMemo(() => {
    const map: Record<string, SocialAccount> = {}
    accounts.forEach((a) => {
      map[a.platform] = a
    })
    return map
  }, [accounts])

  // Initialize selections (captions start empty — user clicks "Write with AI")
  useEffect(() => {
    if (accounts.length > 0) {
      const initialSelected: Record<string, boolean> = {}
      accounts.forEach(account => {
        initialSelected[account.id] = true
      })
      setSelectedAccounts(prev => (Object.keys(prev).length === 0 ? initialSelected : prev))
    }
  }, [accounts])

  const toggleAccount = (accountId: string) => {
    setSelectedAccounts(prev => ({ ...prev, [accountId]: !prev[accountId] }))
  }

  const updateCaption = (accountId: string, text: string) => {
    setCaptions(prev => ({ ...prev, [accountId]: text }))
  }

  // Selected accounts in platform display order
  const selectedAccountsList = useMemo(
    () => PLATFORMS
      .map(p => accountsByPlatform[p.key])
      .filter((a): a is SocialAccount => !!a && selectedAccounts[a.id]),
    [accountsByPlatform, selectedAccounts],
  )

  const currentAccount = selectedAccountsList[composeIndex] || null
  const currentPlatform = currentAccount ? PLATFORMS.find(p => p.key === currentAccount.platform) : null
  const isLastSlide = composeIndex === selectedAccountsList.length - 1

  const hasInstagramSelected = selectedAccountsList.some(a => a.platform === 'instagram_business')
  const instagramNeedsImage = hasInstagramSelected && !eventImagePublicId
  const anySelected = selectedAccountsList.length > 0

  const goToCompose = useCallback(() => {
    setComposeIndex(0)
    setStep('compose')
  }, [])

  const [rewriteError, setRewriteError] = useState<string | null>(null)

  const handleAiRewrite = useCallback((accountId: string, platform: string) => {
    rewriteAccountRef.current = accountId
    setRewriting(true)
    setRewriteError(null)
    fetchAiCaption({ variables: { eventId, platform } })
  }, [eventId, fetchAiCaption])

  const handleUseDescription = useCallback((accountId: string) => {
    const description = captionsData?.generateSocialCaptions?.eventDescription
    if (description) {
      setCaptions(prev => ({ ...prev, [accountId]: description }))
    }
  }, [captionsData])

  const handleShare = async () => {
    const posts = selectedAccountsList.map(account => {
      const transform = PLATFORM_IMAGE_TRANSFORMS[account.platform] || PLATFORM_IMAGE_TRANSFORMS.facebook_page
      const imageUrl = eventImagePublicId ? cloudinaryUrl(eventImagePublicId, transform) : null
      return {
        eventId,
        socialAccountId: account.id,
        caption: captions[account.id] || '',
        imageUrl,
        linkUrl: captionsData?.generateSocialCaptions?.eventLink || null,
        scheduledFor: scheduleMode && scheduledFor ? new Date(scheduledFor).toISOString() : null,
      }
    })

    setPostError(null)
    try {
      const result = await createPosts({ variables: { posts } })
      if (result?.data?.socialPostCreate?.errors?.length > 0) {
        setPostError(result.data.socialPostCreate.errors.map((e: { message: string }) => e.message).join(', '))
        return
      }
      setStep('posted')
    } catch (err) {
      const message = err instanceof Error ? err.message : null
      setPostError(message || 'Something went wrong. Please try again.')
    }
  }

  const isCustomerUrl = captionsData?.generateSocialCaptions?.isCustomerUrl ?? true
  const loading = captionsLoading || accountsLoading
  const scheduleMissing = scheduleMode && !scheduledFor

  // ── Posted success ──
  if (step === 'posted') {
    return (
      <Overlay>
        <Modal>
          <SuccessState>
            <SuccessCheckIcon>
              <Hugeicon name="checkmark-circle" size={56} style={{ color: '#22c55e' }} />
            </SuccessCheckIcon>
            <SuccessTitle>{scheduleMode ? 'Posts Scheduled!' : 'Posts Sent!'}</SuccessTitle>
            <SuccessText>
              Your event has been {scheduleMode ? 'scheduled for posting' : 'shared'} to{' '}
              {selectedAccountsList.map(a => PLATFORMS.find(p => p.key === a.platform)?.label).join(' and ')}.
            </SuccessText>
            <DoneButton onClick={onClose}>Done</DoneButton>
          </SuccessState>
        </Modal>
      </Overlay>
    )
  }

  // ── Step 2: Compose carousel ──
  if (step === 'compose' && currentAccount && currentPlatform) {
    const caption = captions[currentAccount.id] || ''
    const isInstagram = currentAccount.platform === 'instagram_business'
    const previewTransform = PREVIEW_IMAGE_TRANSFORMS[currentAccount.platform]
    const previewImageUrl = eventImagePublicId ? cloudinaryUrl(eventImagePublicId, previewTransform) : null
    const eventLink = captionsData?.generateSocialCaptions?.eventLink || ''
    let linkHostname = 'link'
    try {
      linkHostname = new URL(eventLink).hostname
    } catch {
      // invalid URL — keep default
    }
    const accountName = currentAccount.platformAccountName || currentPlatform.label

    return (
      <Overlay>
        <Modal>
          {/* Compose header */}
          <ComposeHeader>
            <BackButton onClick={() => setStep('select')}>
              <ArrowLeftIcon /> Platforms
            </BackButton>
            <ComposeHeaderCenter>
              <PlatformBadge style={{ color: currentPlatform.color }}>
                {currentPlatform.icon}
                <span>{currentPlatform.label}</span>
              </PlatformBadge>
              {selectedAccountsList.length > 1 && (
                <SlideIndicator>{composeIndex + 1} of {selectedAccountsList.length}</SlideIndicator>
              )}
            </ComposeHeaderCenter>
            <ComposeHeaderRight />
          </ComposeHeader>

          <ComposeBody>
            {/* Caption editor — primary focus */}
            <CaptionSection>
              <CaptionLabelRow>
                <CaptionLabel>Caption</CaptionLabel>
                <CaptionLabelActions>
                  {captionsData?.generateSocialCaptions?.eventDescription && (
                    <UseDescriptionButton
                      onClick={() => handleUseDescription(currentAccount.id)}
                      title="Use event description as caption"
                    >
                      {ClipboardIcon()} Use Description
                    </UseDescriptionButton>
                  )}
                  <AiRewriteButton
                    onClick={() => handleAiRewrite(currentAccount.id, currentAccount.platform)}
                    disabled={rewriting}
                  >
                    {rewriting ? (
                      <>{SparklesIcon()} Generating...</>
                    ) : (
                      <>{SparklesIcon()} Generate</>
                    )}
                  </AiRewriteButton>
                  {caption.length > 0 && (
                    <CharCount over={caption.length > currentPlatform.maxChars}>
                      {caption.length.toLocaleString()} / {currentPlatform.maxChars.toLocaleString()}
                    </CharCount>
                  )}
                </CaptionLabelActions>
              </CaptionLabelRow>
              <CaptionTextarea
                value={caption}
                onChange={e => updateCaption(currentAccount.id, e.target.value)}
                rows={4}
                placeholder={`Write a caption for ${currentPlatform.label}...`}
              />
              {rewriteError && <RewriteErrorText>{rewriteError}</RewriteErrorText>}
              <CaptionHint>{currentPlatform.hint}</CaptionHint>
            </CaptionSection>

            {!isCustomerUrl && (
              <WebsiteUrlWarning>
                No website URL configured — links will point to CivicLift.
              </WebsiteUrlWarning>
            )}

            {isInstagram && !previewImageUrl && (
              <WarningNote>Instagram requires an image to publish.</WarningNote>
            )}

            {/* Compact post preview — styled like actual platform */}
            <PreviewLabel>Post Preview</PreviewLabel>
            <PreviewCard>
              {/* Facebook-style: avatar + name + caption + image + link */}
              {!isInstagram ? (
                <>
                  <PreviewHeader>
                    {currentAccount.platformAccountImageUrl ? (
                      <PreviewAvatar src={currentAccount.platformAccountImageUrl} alt="" />
                    ) : (
                      <PreviewAvatarPlaceholder style={{ background: currentPlatform.color }}>
                        {(accountName)[0]}
                      </PreviewAvatarPlaceholder>
                    )}
                    <div>
                      <PreviewAccountName>{accountName}</PreviewAccountName>
                      <PreviewTimestamp>Just now</PreviewTimestamp>
                    </div>
                  </PreviewHeader>
                  {caption && (
                    <PreviewCaptionText>
                      {caption.length > 150 ? `${caption.slice(0, 150)}...` : caption}
                    </PreviewCaptionText>
                  )}
                  {previewImageUrl && (
                    <PreviewImageCompact ratio="1.91 / 1">
                      <img src={previewImageUrl} alt="" onError={e => {
                        (e.target as HTMLImageElement).style.display = 'none'
                      }} />
                    </PreviewImageCompact>
                  )}
                  {eventLink && (
                    <PreviewLinkBar>
                      <PreviewLinkDomain>{linkHostname.toUpperCase()}</PreviewLinkDomain>
                      <PreviewLinkTitle>{eventTitle}</PreviewLinkTitle>
                    </PreviewLinkBar>
                  )}
                  <PreviewActions>
                    <FbAction>{ThumbsUpIcon()} Like</FbAction>
                    <FbAction>{CommentIcon()} Comment</FbAction>
                    <FbAction>{ShareIcon()} Share</FbAction>
                  </PreviewActions>
                </>
              ) : (
                <>
                  {/* Instagram-style: avatar + name + image + actions + caption */}
                  <PreviewHeader>
                    {currentAccount.platformAccountImageUrl ? (
                      <PreviewAvatar src={currentAccount.platformAccountImageUrl} alt="" />
                    ) : (
                      <PreviewAvatarPlaceholder style={{ background: currentPlatform.color }}>
                        {(accountName)[0]}
                      </PreviewAvatarPlaceholder>
                    )}
                    <PreviewAccountName>{accountName}</PreviewAccountName>
                  </PreviewHeader>
                  {previewImageUrl && (
                    <PreviewImageCompact ratio="1 / 1">
                      <img src={previewImageUrl} alt="" onError={e => {
                        (e.target as HTMLImageElement).style.display = 'none'
                      }} />
                    </PreviewImageCompact>
                  )}
                  <IgActions>
                    <IgActionGroup>
                      <IgAction>{HeartIcon()}</IgAction>
                      <IgAction>{CommentIcon()}</IgAction>
                      <IgAction>{ShareIcon()}</IgAction>
                    </IgActionGroup>
                    <IgAction>{BookmarkIcon()}</IgAction>
                  </IgActions>
                  {caption && (
                    <PreviewCaptionText>
                      <strong>{accountName}</strong>{' '}
                      {caption.length > 120 ? `${caption.slice(0, 120)}...` : caption}
                    </PreviewCaptionText>
                  )}
                </>
              )}
            </PreviewCard>
          </ComposeBody>

          {/* Compose footer */}
          <ComposeFooter>
            {postError && <PostError>{postError}</PostError>}

            <ComposeFooterButtons>
              {composeIndex > 0 && (
                <NavButton onClick={() => setComposeIndex(i => i - 1)}>
                  <ArrowLeftIcon /> Previous
                </NavButton>
              )}
              <FooterSpacer />
              {!isLastSlide ? (
                <NextButton onClick={() => setComposeIndex(i => i + 1)} platformColor={currentPlatform.color}>
                  Next: {PLATFORMS.find(p => p.key === selectedAccountsList[composeIndex + 1]?.platform)?.label}
                  {' '}<ArrowRightIcon />
                </NextButton>
              ) : (
                <>
                  {/* Timing toggle */}
                  <TimingToggle>
                    <TimingOption active={!scheduleMode} onClick={() => setScheduleMode(false)}>
                      Post Now
                    </TimingOption>
                    <TimingOption active={scheduleMode} onClick={() => setScheduleMode(true)}>
                      {CalendarIcon()} Schedule
                    </TimingOption>
                  </TimingToggle>
                  <ShareAllButton
                    onClick={() => setConfirmingPost(true)}
                    disabled={posting || instagramNeedsImage || scheduleMissing}
                  >
                    {posting
                      ? 'Posting...'
                      : scheduleMode
                        ? `Schedule ${selectedAccountsList.length > 1 ? `(${selectedAccountsList.length})` : ''}`
                        : `Post ${selectedAccountsList.length > 1 ? `(${selectedAccountsList.length})` : ''}`
                    }
                    {!posting && <ArrowRightIcon />}
                  </ShareAllButton>
                </>
              )}
            </ComposeFooterButtons>
            {scheduleMode && isLastSlide && (
              <ScheduleDateRow>
                <ScheduleDateLabel>Scheduled for:</ScheduleDateLabel>
                <ScheduleDateInput
                  type="datetime-local"
                  value={scheduledFor}
                  min={new Date().toISOString().slice(0, 16)}
                  onChange={e => setScheduledFor(e.target.value)}
                />
              </ScheduleDateRow>
            )}
          </ComposeFooter>

          {/* Confirmation dialog */}
          {confirmingPost && (
            <ConfirmOverlay>
              <ConfirmDialog>
                <ConfirmTitle>
                  {scheduleMode ? 'Schedule posts?' : 'Post now?'}
                </ConfirmTitle>
                <ConfirmText>
                  This will {scheduleMode ? 'schedule posts' : 'publish'} to{' '}
                  {selectedAccountsList.map(a => PLATFORMS.find(p => p.key === a.platform)?.label).join(' and ')}.
                </ConfirmText>
                <ConfirmButtons>
                  <ConfirmCancelButton onClick={() => setConfirmingPost(false)}>
                    Cancel
                  </ConfirmCancelButton>
                  <ConfirmPostButton
                    onClick={() => {
                      setConfirmingPost(false)
                      handleShare()
                    }}
                    disabled={posting}
                  >
                    {scheduleMode ? 'Schedule' : 'Post Now'}
                  </ConfirmPostButton>
                </ConfirmButtons>
              </ConfirmDialog>
            </ConfirmOverlay>
          )}
        </Modal>
      </Overlay>
    )
  }

  // ── Step 1: Select platforms ──
  return (
    <Overlay>
      <Modal>
        <SubmittedBanner>
          <BannerCheckIcon>
            <Hugeicon name="checkmark-circle" size={32} style={{ color: '#22c55e' }} />
          </BannerCheckIcon>
          <BannerContent>
            <BannerTitle>Event Submitted!</BannerTitle>
            <BannerEventName>&ldquo;{eventTitle}&rdquo;</BannerEventName>
          </BannerContent>
          <BannerDoneButton onClick={onClose}>Done</BannerDoneButton>
        </SubmittedBanner>

        <SelectSection>
          <SelectHeader>Also share to social media?</SelectHeader>
          <SelectHint>Select the platforms you&apos;d like to post to. You can also do this later from your dashboard.</SelectHint>

          {(captionsError || accountsError) && (
            <WarningNote>Failed to load social accounts. You can share later from your dashboard.</WarningNote>
          )}

          {loading && <LoadingText>Loading social accounts...</LoadingText>}

          {!loading && PLATFORMS.map(platform => {
            const account = accountsByPlatform[platform.key]
            const connected = !!account
            const selected = connected && (selectedAccounts[account.id] || false)

            return (
              <SelectPlatformRow
                key={platform.key}
                onClick={() => connected && toggleAccount(account.id)}
                connected={connected}
              >
                <CheckboxWrapper selected={selected} disabled={!connected} platformColor={platform.color}>
                  {selected && (
                    <Hugeicon name="tick" size={12} style={{ color: 'white' }} />
                  )}
                  <HiddenCheckbox
                    type="checkbox"
                    checked={selected}
                    disabled={!connected}
                    onClick={e => e.stopPropagation()}
                    onChange={() => connected && toggleAccount(account.id)}
                  />
                </CheckboxWrapper>
                <SelectPlatformIcon style={{ color: connected ? platform.color : '#9ca3af' }}>
                  {platform.icon}
                </SelectPlatformIcon>
                <SelectPlatformLabel>
                  <SelectPlatformName>{platform.label}</SelectPlatformName>
                  <SelectAccountName connected={connected}>
                    {connected ? account.platformAccountName || platform.label : 'Not connected'}
                  </SelectAccountName>
                </SelectPlatformLabel>
              </SelectPlatformRow>
            )
          })}

          {!loading && instagramNeedsImage && (
            <WarningNote>Instagram requires an image. You can still post to Facebook.</WarningNote>
          )}

          {!loading && anySelected && (
            <ComposeButton onClick={goToCompose} disabled={instagramNeedsImage && selectedAccountsList.every(a => a.platform === 'instagram_business')}>
              Compose Posts <ArrowRightIcon />
            </ComposeButton>
          )}
        </SelectSection>
      </Modal>
    </Overlay>
  )
}

export default SocialShareModal

/* ── Icons ── */

function FacebookIcon() {
  return <Hugeicon name="facebook" size={18} />
}

function InstagramIcon() {
  return <Hugeicon name="instagram" size={18} />
}

function SparklesIcon() {
  return <Hugeicon name="sparkles" size={14} />
}

function ArrowLeftIcon() {
  return <Hugeicon name="arrow-left-long" size={14} />
}

function ArrowRightIcon() {
  return <Hugeicon name="arrow-right-long" size={14} />
}

function ThumbsUpIcon() {
  return <Hugeicon name="thumbs-up" size={16} />
}

function CommentIcon() {
  return <Hugeicon name="comment" size={16} />
}

function ShareIcon() {
  return <Hugeicon name="share" size={16} />
}

function HeartIcon() {
  return <Hugeicon name="favourite" size={20} />
}

function BookmarkIcon() {
  return <Hugeicon name="bookmark" size={20} />
}

function CalendarIcon() {
  return <Hugeicon name="calendar" size={13} />
}

function ClipboardIcon() {
  return <Hugeicon name="clipboard" size={13} />
}

/* ── Shared ── */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
`

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`

const WarningNote = styled.div`
  padding: 10px 14px;
  margin-bottom: 12px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
`

const WebsiteUrlWarning = styled.div`
  padding: 10px 14px;
  margin-top: 12px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
`

const PostError = styled.div`
  width: 100%;
  font-size: 13px;
  color: #dc2626;
  margin-bottom: 8px;
`

/* ── Step 1: Select platforms ── */

const SubmittedBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  background: #f0fdf4;
  border-bottom: 1px solid #bbf7d0;
  border-radius: 16px 16px 0 0;
`

const BannerCheckIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`

const BannerContent = styled.div`
  flex: 1;
  min-width: 0;
`

const BannerTitle = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #166534;
  line-height: 1.2;
`

const BannerEventName = styled.div`
  font-size: 13px;
  color: #15803d;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const BannerDoneButton = styled.button`
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #166534;
  background: white;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background: #f0fdf4; border-color: #86efac; }
`

const SelectSection = styled.div`
  padding: 20px 24px 24px;
`

const SelectHeader = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #102638;
  margin: 0 0 4px;
`

const SelectHint = styled.p`
  font-size: 13px;
  color: #9ca3af;
  margin: 0 0 16px;
`

const LoadingText = styled.div`
  padding: 24px 0;
  text-align: center;
  font-size: 14px;
  color: #6b7280;
`

const SelectPlatformRow = styled.div<{ connected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  margin-bottom: 10px;
  cursor: ${({ connected }) => (connected ? 'pointer' : 'default')};
  opacity: ${({ connected }) => (connected ? 1 : 0.5)};
  user-select: none;
  transition: border-color 0.15s ease;
  &:hover { border-color: ${({ connected }) => (connected ? '#d1d5db' : '#e5e7eb')}; }
`

const CheckboxWrapper = styled.span<{ selected: boolean; disabled: boolean; platformColor: string }>`
  position: relative;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 2px solid ${({ selected, disabled, platformColor }) =>
    disabled ? '#d1d5db' : selected ? platformColor : '#9ca3af'};
  background: ${({ selected, platformColor }) => (selected ? platformColor : 'white')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  transition: all 0.15s ease;
`

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: inherit;
  margin: 0;
`

const SelectPlatformIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const SelectPlatformLabel = styled.div`
  flex: 1;
  min-width: 0;
`

const SelectPlatformName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`

const SelectAccountName = styled.div<{ connected: boolean }>`
  font-size: 12px;
  color: ${({ connected }) => (connected ? '#6b7280' : '#9ca3af')};
  font-style: ${({ connected }) => (connected ? 'normal' : 'italic')};
  margin-top: 1px;
`

const ComposeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 16px;
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 600;
  color: white;
  background: #1877f2;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  &:hover { background: #166fe5; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

/* ── Step 2: Compose carousel ── */

const ComposeHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 20px;
  border-bottom: 1px solid #e5e7eb;
  gap: 12px;
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 0;
  &:hover { color: #374151; }
`

const ComposeHeaderCenter = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`

const ComposeHeaderRight = styled.div`
  width: 70px;
`

const PlatformBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 700;
`

const SlideIndicator = styled.span`
  font-size: 12px;
  color: #9ca3af;
  font-weight: 400;
`

const ComposeBody = styled.div`
  padding: 20px;
`

/* ── Compact post preview ── */

const PreviewLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 20px 0 8px;
`

const PreviewCard = styled.div`
  border: 1px solid #dadde1;
  border-radius: 10px;
  overflow: hidden;
  background: white;
  font-size: 13px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
`

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
`

const PreviewAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`

const PreviewAvatarPlaceholder = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
`

const PreviewAccountName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #050505;
`

const PreviewTimestamp = styled.div`
  font-size: 12px;
  color: #65676b;
  line-height: 1.2;
`

const PreviewImageCompact = styled.div<{ ratio: string }>`
  width: 100%;
  aspect-ratio: ${({ ratio }) => ratio};
  max-height: 200px;
  overflow: hidden;
  background: #000;
  & > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`

const PreviewCaptionText = styled.div`
  padding: 8px 12px;
  font-size: 13px;
  line-height: 1.4;
  color: #050505;
  overflow: hidden;
  max-height: calc(1.4em * 3 + 16px);
  word-break: break-word;
`

const PreviewLinkBar = styled.div`
  padding: 8px 12px;
  background: #f0f2f5;
  border-top: 1px solid #dadde1;
`

const PreviewLinkDomain = styled.div`
  font-size: 11px;
  color: #65676b;
  letter-spacing: 0.03em;
  text-transform: uppercase;
`

const PreviewLinkTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #050505;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PreviewActions = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #65676b;
  border-top: 1px solid #dadde1;
`

const FbAction = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: default;
`

const IgActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
`

const IgActionGroup = styled.div`
  display: flex;
  gap: 14px;
`

const IgAction = styled.span`
  display: flex;
  align-items: center;
  color: #262626;
  cursor: default;
`

/* ── Caption editor ── */

const CaptionSection = styled.div``

const CaptionLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`

const CaptionLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
`

const CaptionLabelActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const UseDescriptionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
  &:hover { background: #e5e7eb; border-color: #9ca3af; }
`

const AiRewriteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #7c3aed;
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
  &:hover { background: #ede9fe; border-color: #c4b5fd; }
  &:disabled { opacity: 0.6; cursor: wait; }
`

const CharCount = styled.span<{ over?: boolean }>`
  font-size: 11px;
  color: ${({ over }) => (over ? '#dc2626' : '#9ca3af')};
`

const CaptionTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.6;
  resize: vertical;
  color: #374151;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`

const RewriteErrorText = styled.div`
  font-size: 12px;
  color: #b45309;
  margin-top: 6px;
`

const CaptionHint = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
`

/* ── Compose footer ── */

const ComposeFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
`

const TimingToggle = styled.div`
  display: flex;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
`

const TimingOption = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: ${({ active }) => (active ? '600' : '500')};
  color: ${({ active }) => (active ? '#102638' : '#6b7280')};
  background: ${({ active }) => (active ? 'white' : 'transparent')};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: ${({ active }) => (active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none')};
  &:hover { color: #374151; }
`

const ScheduleDateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
`

const ScheduleDateLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
`

const ScheduleDateInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  color: #374151;
  &:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
`

const ComposeFooterButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const FooterSpacer = styled.div`
  flex: 1;
`

const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: #f9fafb; }
`

const NextButton = styled.button<{ platformColor: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: ${({ platformColor }) => platformColor};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  &:hover { opacity: 0.9; }
`

const ShareAllButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 22px;
  font-size: 15px;
  font-weight: 700;
  color: white;
  background: #22c55e;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
  transition: all 0.15s ease;
  &:hover { background: #16a34a; box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4); transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; box-shadow: none; transform: none; }
`

/* ── Posted success ── */

const SuccessState = styled.div`
  text-align: center;
  padding: 48px 32px;
`

const SuccessCheckIcon = styled.div`
  margin-bottom: 16px;
`

const SuccessTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #102638;
  margin: 0 0 8px;
`

const SuccessText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 24px;
`

const DoneButton = styled.button`
  padding: 10px 32px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: #22c55e;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: #16a34a; }
`

/* ── Confirmation dialog ── */

const ConfirmOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  z-index: 10;
`

const ConfirmDialog = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 340px;
  width: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  text-align: center;
`

const ConfirmTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: #102638;
  margin: 0 0 8px;
`

const ConfirmText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 20px;
  line-height: 1.5;
`

const ConfirmButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`

const ConfirmCancelButton = styled.button`
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: #f9fafb; }
`

const ConfirmPostButton = styled.button`
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: #22c55e;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: #16a34a; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`
